import { ModalFunctions, UploadFile } from 'fave-ui';
import { Paperclip } from 'phosphor-react';
import style from './style.module.css';
import { FormField, FormFieldsRecord } from './types';

export const showRemoveFileModal = <T extends {}>({
  file,
  modalTitle = 'Remove Item?',
  modalContent = 'Are you sure you want to remove item?',
  onOk,
}: {
  file: UploadFile<T>;
  modalTitle?: string;
  modalContent?: string;
  onOk?: (
    resolve: (value: void | PromiseLike<void>) => void,
    reject: () => void,
  ) => void;
}): Promise<void> =>
  new Promise((resolve, reject) =>
    ModalFunctions.confirm({
      centered: true,
      title: modalTitle,
      content: (
        <div className={style.onRemoveFileModal}>
          <p>{modalContent}</p>
          <div className={style.fileName}>
            <Paperclip size={16} />
            <span>{file.name}</span>
          </div>
        </div>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: onOk ? () => onOk(resolve, reject) : () => resolve(),
      onCancel: () => reject(),
    }),
  );

export const showUnselectPaymentModeModal = ({
  modalTitle = 'Disable payment option?',
  modalContent = 'Are you sure you want to disable? It will be disabled on FastPay website as well.',
  onOk,
}: {
  modalTitle?: string;
  modalContent?: string;
  onOk?: () => void;
}): Promise<void> =>
  new Promise((resolve, reject) =>
    ModalFunctions.confirm({
      centered: true,
      title: modalTitle,
      content: (
        <div className={style.onRemoveFileModal}>
          <p>{modalContent}</p>
        </div>
      ),
      okText: 'Disable',
      cancelText: 'Cancel',
      onOk: onOk ? () => resolve(onOk()) : () => resolve(),
      onCancel: () => reject(),
    }),
  );

export const createObjectFromKey = <K extends keyof FormField>(
  data: FormFieldsRecord,
  keyToAccess: K,
): Record<string, FormField[K]> => {
  return Object.keys(data).reduce(
    (acc, currentKey) => {
      const field = data[currentKey];
      acc[field.key] = field[keyToAccess];
      return acc;
    },
    {} as Record<string, FormField[K]>,
  );
};

const fetchImageAsBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return blob;
};

// fetch image as file using url to be used as payload in form data
export const fetchImageAsFile = async (
  url: string,
  fileName: string,
): Promise<File> => {
  const blob = await fetchImageAsBlob(url);
  const file = new File([blob], fileName, { type: blob.type });
  return file;
};

// get file format from url, since BE returns url containing file name and format
export const getFileFormat = (url: string): string | undefined => {
  const fileNameArray = url.split('.');
  return fileNameArray.length > 1
    ? fileNameArray[fileNameArray.length - 1]
    : undefined;
};
